import React from 'react';
import { format } from 'date-fns';
import Field from '../../../../components/Field';
import TextInput from '../../../../components/TextInput';
import Dropdown from '../../../../components/Dropdown';
import PanelHeading from '../../../../components/PanelHeading';
import {
  Employee,
  EmployeeFormData,
  FormErrors,
  EMPLOYEE_TYPE_OPTIONS,
  ROLE_OPTIONS,
  TeamMemberType,
  ManagerialRole
} from '../../config';

interface DetailsTabProps {
  employee: Employee;
  draft: EmployeeFormData;
  errors: FormErrors;
  disabled: boolean;
  onChange: (patch: Partial<EmployeeFormData>) => void;
  onToggleActive: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  employee,
  draft,
  errors,
  disabled,
  onChange,
  onToggleActive
}) => (
  <div className="employee-tab">
    <PanelHeading title="Person" />
    <div className="pdm-grid pdm-grid--2">
      <Field label="First name" htmlFor="emp-first-name" required error={errors.first_name}>
        <TextInput
          id="emp-first-name"
          value={draft.first_name}
          invalid={!!errors.first_name}
          disabled={disabled}
          onChange={(e) => onChange({ first_name: e.target.value })}
        />
      </Field>
      <Field label="Last name" htmlFor="emp-last-name" required error={errors.last_name}>
        <TextInput
          id="emp-last-name"
          value={draft.last_name}
          invalid={!!errors.last_name}
          disabled={disabled}
          onChange={(e) => onChange({ last_name: e.target.value })}
        />
      </Field>
      <Field label="Employee type" required error={errors.type}>
        <Dropdown
          items={EMPLOYEE_TYPE_OPTIONS}
          value={draft.type}
          onChange={(value: string) => onChange({ type: value as TeamMemberType })}
          placeholder="Select type"
          disabled={disabled}
        />
      </Field>
      <Field label="Role" required error={errors.role}>
        <Dropdown
          items={ROLE_OPTIONS}
          value={draft.role}
          onChange={(value: string) => onChange({ role: value as ManagerialRole })}
          placeholder="Select role"
          disabled={disabled}
        />
      </Field>
      <Field label="Email address" htmlFor="emp-email" className="span-all" error={errors.email}>
        <TextInput
          id="emp-email"
          type="email"
          value={draft.email}
          invalid={!!errors.email}
          disabled={disabled}
          onChange={(e) => onChange({ email: e.target.value })}
        />
      </Field>
    </div>

    <PanelHeading
      title="Working day"
      spaced
      hint="Sets up this person's timesheet. Contracted hours per week, which leave is earned on, are on the Leave tab."
    />
    <div className="pdm-grid pdm-grid--3">
      <Field
        label="Standard hours per day"
        htmlFor="emp-day-hours"
        required
        error={errors.standard_hours_per_day}
        hint="Sets the daily and weekly targets on their timesheet, and the length of a day when leave is shown in days."
      >
        <TextInput
          id="emp-day-hours"
          type="number"
          step="0.1"
          min="0.5"
          max="24"
          value={draft.standard_hours_per_day}
          invalid={!!errors.standard_hours_per_day}
          disabled={disabled}
          onChange={(e) => onChange({ standard_hours_per_day: e.target.value })}
        />
      </Field>
      <Field
        label="Usual start time"
        htmlFor="emp-start-time"
        required
        error={errors.default_start_time}
        hint="Where the first entry of a day starts in the timesheet's Day view."
      >
        <TextInput
          id="emp-start-time"
          type="time"
          step="900"
          value={draft.default_start_time}
          invalid={!!errors.default_start_time}
          disabled={disabled}
          onChange={(e) => onChange({ default_start_time: e.target.value })}
        />
      </Field>
      <Field
        label="Lunch break"
        htmlFor="emp-lunch-start"
        required
        error={errors.lunch}
        hint="Not worked: a timed entry that runs through it loses that time. 0 minutes switches this off."
      >
        <div className="employee-tab__lunch">
          <TextInput
            id="emp-lunch-start"
            type="time"
            step="900"
            value={draft.lunch_start_time}
            aria-label="Lunch starts"
            invalid={!!errors.lunch}
            disabled={disabled}
            onChange={(e) => onChange({ lunch_start_time: e.target.value })}
          />
          <TextInput
            type="number"
            step="15"
            min="0"
            max="240"
            value={draft.lunch_minutes}
            aria-label="Lunch minutes"
            invalid={!!errors.lunch}
            disabled={disabled}
            trailing={<span className="employee-tab__unit">min</span>}
            onChange={(e) => onChange({ lunch_minutes: e.target.value })}
          />
        </div>
      </Field>
    </div>

    <PanelHeading title="Record" spaced />
    <dl className="field-list">
      <div className="field-list__row">
        <dt className="field-list__label">Status</dt>
        <dd className="field-list__value">{employee.is_active ? 'Active' : 'Inactive'}</dd>
      </div>
      <div className="field-list__row">
        <dt className="field-list__label">Added</dt>
        <dd className="field-list__value">{format(new Date(employee.created_at), 'd MMM yyyy')}</dd>
      </div>
    </dl>

    <PanelHeading title={employee.is_active ? 'Deactivate' : 'Reactivate'} spaced />
    {employee.is_active ? (
      <div className="danger-card">
        <div className="danger-card__text">
          <p className="danger-card__title">Deactivate this employee</p>
          <p className="danger-card__body">
            Blocks them from signing in and removes them from active lists. Their
            data and history are preserved, and they stop earning leave from today.
          </p>
        </div>
        <button
          type="button"
          className="pill-btn pill-btn--danger"
          disabled={disabled}
          onClick={onToggleActive}
        >
          Deactivate
        </button>
      </div>
    ) : (
      <div className="ui-row">
        <span className="employee-tab__row-text">
          Restores them to active status, able to sign in and be assigned to
          projects again. They earn leave again from today.
        </span>
        <span className="ui-row__spacer" />
        <button
          type="button"
          className="pill-btn pill-btn--primary"
          disabled={disabled}
          onClick={onToggleActive}
        >
          Reactivate
        </button>
      </div>
    )}
  </div>
);

export default DetailsTab;
